/**
 * useCmsArticles — published articles, fresher than the bundle.
 *
 * Mirrors `useCmsLectures`: the build-time snapshot (`articles.generated.ts`)
 * seeds the state so the prerendered HTML and the no-JS case are complete,
 * and a runtime read of `/api/cms/public/articles` replaces it on mount.
 *
 * WHY THIS EXISTS FOR THE LECTURES PAGE
 * --------------------------------------
 * The 「總監專欄」 tab on /lectures lists the Executive Director's articles.
 * It reads from the build-time `cmsArticles` snapshot like every other
 * consumer, but that snapshot is frozen at build time — an article published
 * in the CMS would not appear until the next deploy. That is the exact
 * incident `useCmsLectures` was written for (2026-10-16: "published a lecture
 * in the CMS, it shows on the dev box but not on prod").
 *
 * SELECTING BY TAG, NOT BY SLUG
 * -----------------------------
 * The column is identified by the `總監專欄` tag, which the CMS operator
 * sets on the article. Hard-coding slugs would mean every new column
 * article needs a code change and a deploy to appear.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsArticles, type CmsArticleIndexEntry } from '@/content/cms/articles.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'

interface ArticlesResp {
  articles?: CmsArticleIndexEntry[]
}

/** The tag that marks an article as part of the Executive Director's column. */
export const COLUMN_TAG = '總監專欄'

/**
 * Resolve an article's text for a locale.
 *
 * Chain: requested locale → zh-Hant → en. The server pre-resolves empty
 * columns via OpenCC (see `preResolveOpenCC` in server/routes/public.ts), so
 * the collapsed `title` already carries the converted text for the requested
 * locale.
 */
function pick(
  locale: Locale,
  item: CmsArticleIndexEntry,
  field: 'title' | 'excerpt',
): string {
  const suffix = locale === 'zh-Hant' ? 'zhHant' : locale === 'zh-Hans' ? 'zhHans' : 'en'
  const direct = item[`${field}_${suffix}` as keyof CmsArticleIndexEntry]
  if (typeof direct === 'string' && direct.length > 0) return direct
  const collapsed = item[field]
  return typeof collapsed === 'string' ? collapsed : ''
}

/** An article with its locale already resolved — the shape the page renders. */
export interface ResolvedArticle {
  slug: string
  title: string
  excerpt: string
  coverMediaUuid?: string
  publishedAt?: number
  tags: string[]
}

/**
 * The tags each snapshot article carried at build time, keyed by slug.
 *
 * This is the fallback source for `tags` when the live API does not supply
 * them. See `unionTags` for why that matters.
 */
const snapshotTagsBySlug = new Map<string, string[]>(
  cmsArticles.map((a) => [a.slug, Array.isArray(a.tags) ? a.tags : []]),
)

/**
 * The union of the live and snapshot tag sets for one article.
 *
 * WHY THIS EXISTS
 * --------------
 * On 2026-10-21 the production API (`/api/cms/public/articles`) was still
 * serving a `server/dist/` build compiled on Oct 20 — before the `tags`
 * column was added to the public route. Its JSON response therefore had NO
 * `tags` key at all. `selectColumnArticles` filtered on that tag, matched
 * nothing, and the 「總監專欄」 tab rendered its empty state on the live site
 * even though the CMS held two tagged articles.
 *
 * The build-time snapshot (`articles.generated.ts`) is written by
 * `npm run cms:sync`, which fetches the same endpoint — so a snapshot taken
 * AFTER the backend shipped carries the tags, and a snapshot taken BEFORE it
 * does not.
 *
 * Union rather than "live wins". The tag set is additive metadata: an
 * article carrying MORE tags is still correctly a member of every set it
 * was a member of before. Dropping the snapshot's tags when the live
 * response has a non-empty but shorter list would silently drop the
 * article out of the column — exactly the bug this whole mechanism
 * exists to prevent. And when the live response has NO tags at all, the
 * union is exactly the snapshot's set.
 *
 * This decouples the frontend deploy from the backend deploy: shipping the
 * frontend alone already fixes the empty state.
 */
function unionTags(
  article: CmsArticleIndexEntry,
): CmsArticleIndexEntry {
  const live = Array.isArray(article.tags) ? article.tags : []
  const snapshot = snapshotTagsBySlug.get(article.slug)
  if (!snapshot || snapshot.length === 0) return article
  const merged = [...new Set([...live, ...snapshot])]
  return { ...article, tags: merged }
}

export function useCmsArticles(): ResolvedArticle[] {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const [items, setItems] = useState<CmsArticleIndexEntry[]>(cmsArticles)

  useEffect(() => {
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/articles?locale=${encodeURIComponent(locale)}&limit=200${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<ArticlesResp>) : null))
      .then((j) => {
        if (cancelled) return
        // An empty array means the CMS has no published articles, which is a
        // legitimate operator state — do not blank the page.
        if (!j?.articles?.length) return
        setItems(j.articles.map(unionTags))
      })
      .catch(() => {
        // Network error, abort, or non-JSON response. The snapshot stays.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale])

  return items.map((item) => ({
    slug: item.slug,
    title: pick(locale, item, 'title'),
    excerpt: pick(locale, item, 'excerpt'),
    coverMediaUuid: item.cover_media_uuid ?? undefined,
    publishedAt: item.published_at ?? undefined,
    tags: item.tags ?? [],
  }))
}

/**
 * The articles tagged as part of the Executive Director's column.
 *
 * Matches the tag in any locale-written form. The tag itself is a Chinese
 * string written by the operator in the admin, so it is stored once and
 * compared literally — but the operator may reasonably write it in either
 * Chinese variant, so the comparison accepts both.
 */
export function selectColumnArticles(articles: ResolvedArticle[]): ResolvedArticle[] {
  const variants = ['總監專欄', '总监专栏']
  return articles.filter((a) => a.tags.some((tag) => variants.includes(tag)))
}

/**
 * Newest first, by `published_at`.
 *
 * The API already sorts (`ORDER BY a.published_at DESC, a.id DESC`) and the
 * snapshot preserves that order, so this is normally a no-op. It is here
 * because the column tab slices the list to show the newest N (see
 * LecturesPage) — "newest" must be guaranteed by the component, not assumed
 * from an upstream ORDER BY that a future edit could drop.
 *
 * Articles with no `published_at` sink to the end rather than to the front,
 * and the sort is stable, so equal timestamps keep their incoming order.
 */
export function sortArticlesNewestFirst(
  articles: ResolvedArticle[],
): ResolvedArticle[] {
  return [...articles].sort((a, b) => {
    const at = a.publishedAt ?? Number.NEGATIVE_INFINITY
    const bt = b.publishedAt ?? Number.NEGATIVE_INFINITY
    return bt - at
  })
}
